/**
 * K108-P — approved training-provider domains for Companion provider-tab
 * monitoring (v1.2.0).
 *
 * v1.2.0 ships a STATIC, YouTube-first allowlist that MUST stay in lockstep with
 * the extension manifest `host_permissions` / `content_scripts` matches. A
 * provider URL is only monitorable when its host is on this allowlist; anything
 * else falls back to the unmonitored `window.open` launch (no provider-tab
 * evidence). Optional/dynamic providers are deferred to v1.3.0.
 */
export const APPROVED_PROVIDER_HOSTS: readonly string[] = [
  'www.youtube.com',
  'm.youtube.com',
  'youtu.be',
];

/** Lowercased host of a URL, or null if unparseable. */
export function urlHost(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/** True only for an https URL whose host is on the approved provider allowlist. */
export function isApprovedProviderUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && APPROVED_PROVIDER_HOSTS.includes(u.hostname.toLowerCase());
  } catch {
    return false;
  }
}

/**
 * Approved-domain set to hand to the Companion for a provider URL. Returns the
 * URL's host only when it is approved (else empty). The Companion re-validates
 * against its own manifest hosts, so this is defense-in-depth, not the sole gate.
 */
export function approvedDomainsForUrl(url: string | null | undefined): string[] {
  const host = urlHost(url);
  return host && APPROVED_PROVIDER_HOSTS.includes(host) ? [host] : [];
}

// ── K108-Q: canonical provider RESOURCE matching (domain is not enough) ──────
//
// A training session is tied to a specific YouTube VIDEO, not to youtube.com.
// These normalize the watch / youtu.be / m.youtube forms (ignoring harmless
// params like t=, list=, feature=) to a single stable key `youtube:<videoId>`.
// Video IDs are CASE-SENSITIVE — only the host is lowercased, never the id.
// The extension mirrors this exact algorithm (background.js + providerContentScript.js).

/** Extract the YouTube video id from any approved YouTube URL form, or null. */
export function youtubeVideoId(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    if (host === 'youtu.be') {
      const id = u.pathname.split('/').filter(Boolean)[0];
      return id || null;
    }
    if (host === 'www.youtube.com' || host === 'm.youtube.com' || host === 'youtube.com') {
      const v = u.searchParams.get('v');
      if (v) return v;
      // /embed/<id>, /shorts/<id>, /live/<id>, /v/<id>
      const m = u.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/?#]+)/);
      return m ? m[1] : null;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Canonical resource key for an approved provider URL, e.g. `youtube:dQw4w9WgXcQ`.
 * null when the URL is not an approved provider VIDEO (e.g. the YouTube home
 * page or an unapproved host). Two URLs with the same key are the same video
 * regardless of extra query params.
 */
export function providerResourceKey(url: string | null | undefined): string | null {
  const host = urlHost(url);
  if (!host || !APPROVED_PROVIDER_HOSTS.includes(host)) return null;
  const id = youtubeVideoId(url);
  return id ? `youtube:${id}` : null;
}
